import { createSlice } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';

export const initialState: State = {};

const slice = createSlice({
  name: 'userchat',
  initialState,
  reducers: {},
});

export const api = createApi({
  reducerPath: 'userchatApi',
  baseQuery: fetchBaseQuery({
    baseUrl: 'http://localhost:7500',
    credentials: 'include',
  }),
  tagTypes: ['chat', 'auth', 'user'],
  endpoints: builder => ({
    // Login Endpoint
    loginUser: builder.mutation({
      query: (loginData: { email: string; password: string }) => ({
        url: `/api/v1/user/login`,
        method: 'POST',
        body: loginData,
      }),
      invalidatesTags: ['auth'],
    }),

    // Sign-Up Endpoint
    signUpUser: builder.mutation({
      query: (signUpData: {
        name: string;
        email: string;
        password: string;
      }) => ({
        url: `/api/v1/user/new`,
        method: 'POST',
        body: signUpData,
      }),
      invalidatesTags: ['auth'],
    }),

    // Get All Users (Admin Only)
    getAllUsers: builder.query({
      query: () => ({
        url: `/api/v1/user/getAllUsers`,
        method: 'GET',
      }),
      providesTags: ['user'],
    }),

    // Get Admin List
    getAdminList: builder.query({
      query: () => ({
        url: `/api/v1/user/getAdminList`,
        method: 'GET',
      }),
      providesTags: ['user'],
    }),

    // CHAT Endpoints
    getUserChatList: builder.query({
      query: () => ({
        url: `/api/v1/user/search`,
        method: 'GET',
      }),
      providesTags: ['chat'],
    }),

    getChatMessages: builder.query({
      query: ({ userId, adminId }: { userId: string; adminId: string }) => ({
        url: `/api/v1/chat/getMessages/${adminId}`,
        method: 'GET',
        headers: { userId },
      }),
      providesTags: ['chat'],
    }),

    sendMessage: builder.mutation({
      query: (messageData: {
        sender: string;
        receiver: string;
        content: string;
      }) => ({
        url: `/api/v1/chat/sendMessage`,
        method: 'POST',
        body: messageData,
      }),
      invalidatesTags: ['chat'],
    }),
  }),
});

export const { actions: UserchatActions } = slice;

export const useUserchatSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: UserchatActions,
    useLoginUserMutation: api.useLoginUserMutation,
    useSignUpUserMutation: api.useSignUpUserMutation,

    useLazyGetAllUsersQuery: api.useLazyGetAllUsersQuery,
    useLazyGetAdminListQuery: api.useLazyGetAdminListQuery,
    useLazyGetUserChatListQuery: api.useLazyGetUserChatListQuery,
    useGetChatMessagesQuery: api.useGetChatMessagesQuery,
    useSendMessageMutation: api.useSendMessageMutation,
  };
};
